import { landmineNames } from '@mm/constants/landmine-names';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { useRssFeedCRUDActions } from '@mm/state/actions/RssFeedCRUDActions';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import RssFeedModel from '@mm/state/model/RssFeedModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectAudiences } from '@mm/state/selectors/messengerAdSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { generateBasicStructure } from '@mm/state/helpers/filterable-select-helpers';
import Select from '../../../../../common/components/FilterableSelect/FilterableSelect';
import { ItemLike } from '../../../../../common/components/FlatMenu/FlatMenu';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import { useFetchAudiences } from '@mm/state/hooks/use-fetch-audiences';
import { Errors, isValid } from './RssFeedDetails';
import './RssFeedDetails.scss';

type Props = {
  feed: RssFeedModel;
  chatbotId: number;
  validate: ReturnType<typeof isValid>;
  errors: Errors;
};

export const RssAudienceSelector = (props: Props) => {
  const { feed, chatbotId, validate, errors } = props;

  const history = useHistory();

  const audiences = useSelector(selectAudiences);
  const audienceDetails = useSelector(
    (state: StoreState) => state.audiences.audienceDetails,
  );

  const rssFeedCRUDActions = useRssFeedCRUDActions();
  const audiencesCRUDActions = useAudiencesCRUDActions();
  const modalActions = useModalActions();

  const initialAudience = () => audiences.find(a => a.id === feed?.audienceId);

  const [selectedAudience, setSelectedAudience] = useState(initialAudience);
  const [redirectToAudience, setRedirectToAudience] = useState(false);

  const onCreateAudience = () => {
    audiencesCRUDActions.unselectAudience();
    audiencesCRUDActions.create({ filterParams: {} }, chatbotId);
    setRedirectToAudience(true);
  };

  const canSelectAudiences = useSelector(
    combinedFeatureFlagsSelector,
  ).campaignsAudiencesSelection;

  const onSelectAudience = (
    e: string,
    item: AudiencesModel,
    preventChange: () => void,
  ) => {
    const selectedAudience = audiences.find(item => item.id === Number(e));

    if (!canSelectAudiences && selectedAudience?.messengerAdAudience) {
      preventChange();
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['RSS-FEED__FEED-DETAILS__SELECT-AUDIENCE'],
        feature: 'campaignsAudiencesSelection',
      });
    } else if (selectedAudience && selectedAudience?.id !== feed.audienceId) {
      validate({ audienceId: selectedAudience.id });
      setSelectedAudience(selectedAudience);
      const newFeed = Object.assign({}, feed, {
        audienceId: selectedAudience?.id,
      });
      rssFeedCRUDActions.update(chatbotId, newFeed);
    }
  };

  const showProBanner = (entity: ItemLike) =>
    entity.messengerAdAudience ? 'unicorn' : undefined;

  const { fetch } = useFetchAudiences();
  const [outerValue, setOuterValue] = useState<string>();

  useEffect(() => {
    fetch(1, 10);
  }, []);

  useEffect(() => {
    setOuterValue(feed?.audienceName || 'No Audience');
  }, [feed]);

  useEffect(() => {
    if (redirectToAudience && audienceDetails) {
      const path = BotBuilderRoutes.audiences.getLink({ chatbotId });

      history.push(path);
    }
  }, [redirectToAudience, audienceDetails]);

  return (
    <div className="feed-details__input-container">
      <div className="feed-details__audience-select-container">
        <div className="feed-details__field-title">Audience</div>
        <div>
          <div className="feed-details__audience-select">
            <Select
              outerValue={outerValue}
              onChange={onSelectAudience}
              suggestions={generateBasicStructure(audiences)}
              showProBanner={showProBanner}
            />
          </div>
          <span
            className="feed-details__audience-link"
            onClick={onCreateAudience}
          >
            New Audience
          </span>
        </div>
        <div className="feed-details__error">{errors.audience}</div>
      </div>
    </div>
  );
};
